import React, { useState, useEffect } from 'react';
import { Box, Typography, Stack, LinearProgress, Chip, IconButton, Card, CardContent, Avatar, useTheme } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import CodeIcon from '@mui/icons-material/Code';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DataObjectIcon from '@mui/icons-material/DataObject';
import CloudIcon from '@mui/icons-material/Cloud';
import StorageIcon from '@mui/icons-material/Storage';
import SecurityIcon from '@mui/icons-material/Security';
import ApiIcon from '@mui/icons-material/Api';
import BlockchainNetwork from './BlockchainNetwork';
// import { Skills3DVisualization } from './Interactive3D';

const SectionContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(8, 0),
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden'
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1
}));

// Enhanced Animations
const skillBarAnimation = keyframes`
  from {
    width: 0%;
  }
  to {
    width: var(--target-width);
  }
`;

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(0, 102, 204, 0.3);
  }
  50% {
    box-shadow: 0 0 40px rgba(0, 102, 204, 0.6), 0 0 60px rgba(0, 212, 170, 0.3);
  }
`;

// Modern Skill Card
const ModernSkillCard = styled(Card)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
    transition: 'left 0.6s ease'
  },
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 20px 60px rgba(0, 102, 204, 0.3)'
      : '0 20px 60px rgba(0, 0, 0, 0.15)',
    borderColor: theme.palette.primary.main,
    '&::before': {
      left: '100%'
    }
  }
}));

// Tech Icon with Animation
const TechIcon = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== 'isActive'
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  width: '70px',
  height: '70px',
  background: isActive
    ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
    : theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)',
  color: isActive ? '#ffffff' : theme.palette.text.primary,
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  animation: isActive ? `${floatAnimation} 3s ease-in-out infinite` : 'none',
  '&:hover': {
    transform: 'scale(1.1) rotate(5deg)',
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    color: '#ffffff',
    animation: `${pulseGlow} 2s ease-in-out infinite`
  }
}));

// Animated Progress Bar
const AnimatedSkillBar = styled(LinearProgress, {
  shouldForwardProp: (prop) => prop !== 'targetValue' && prop !== 'isVisible'
})<{ targetValue: number; isVisible: boolean }>(({ theme, targetValue, isVisible }) => ({
  height: '12px',
  borderRadius: '6px',
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(0, 0, 0, 0.05)',
  overflow: 'hidden',
  position: 'relative',
  '& .MuiLinearProgress-bar': {
    borderRadius: '6px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    position: 'relative',
    '--target-width': `${targetValue}%`,
    animation: isVisible ? `${skillBarAnimation} 1.5s ease-out forwards` : 'none',
    width: isVisible ? `${targetValue}%` : '0%',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
      animation: 'shimmer 2s ease-in-out infinite'
    }
  },
  '@keyframes shimmer': {
    '0%': {
      transform: 'translateX(-100%)'
    },
    '100%': {
      transform: 'translateX(100%)'
    }
  }
}));

// Skill Category Header
const CategoryHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  background: theme.palette.mode === 'dark' 
    ? 'rgba(0, 102, 204, 0.1)' 
    : 'rgba(0, 102, 204, 0.05)',
  borderRadius: '16px',
  border: `1px solid ${theme.palette.primary.main}40`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '4px',
    height: '100%',
    background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
  }
}));

const SkillProgress = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3)
}));

interface SkillItemProps {
  name: string;
  level: number;
  icon: React.ReactNode;
  experience: string;
  isVisible: boolean;
}

interface TechIconProps {
  icon: React.ReactNode;
  name: string;
  isActive: boolean;
  onClick: () => void;
}

const ModernSkillItem: React.FC<SkillItemProps> = ({ name, level, icon, experience, isVisible }) => (
  <SkillProgress>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="body1" fontWeight={600}>
        {name}
      </Typography>
          <Typography variant="caption" color="text.secondary">
            {experience}
          </Typography>
        </Box>
      </Stack>
      <Typography variant="h6" fontWeight={700} color="primary.main">
        {level}%
      </Typography>
    </Stack>
    <AnimatedSkillBar 
      variant="determinate" 
      value={level} 
      targetValue={level}
      isVisible={isVisible}
    />
  </SkillProgress>
);

const TechIconComponent: React.FC<TechIconProps> = ({ icon, name, isActive, onClick }) => (
  <Box sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={onClick}>
    <TechIcon isActive={isActive}>
      {icon}
    </TechIcon>
    <Typography 
      variant="caption" 
      sx={{ 
        mt: 1, 
        display: 'block', 
        fontWeight: isActive ? 600 : 400,
        color: isActive ? 'primary.main' : 'text.secondary'
      }}
    >
      {name}
    </Typography>
  </Box>
);

const AboutSection: React.FC = () => {
  const theme = useTheme();
  const [activeCategory, setActiveCategory] = useState('frontend');
  const [skillsVisible, setSkillsVisible] = useState(false);
  const [activeTech, setActiveTech] = useState<string | null>(null);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setSkillsVisible(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    const skillsSection = document.getElementById('skills-section');
    if (skillsSection) {
      observer.observe(skillsSection);
    }

    return () => observer.disconnect();
  }, []);

  const skillCategories = {
    frontend: {
      title: 'Frontend Development',
      icon: <CodeIcon sx={{ fontSize: 28, color: 'primary.main' }} />,
      skills: [
        {
          name: 'React & Next.js',
          level: 85,
          icon: <DataObjectIcon color="primary" />,
          experience: '2+ years'
        },
        {
          name: 'TypeScript',
          level: 80,
          icon: <DataObjectIcon color="primary" />,
          experience: '1.5+ years'
        },
        {
          name: 'Material-UI & Tailwind',
          level: 75,
          icon: <CodeIcon color="primary" />,
          experience: '2+ years'
        }
      ]
    },
    backend: {
      title: 'Backend & AI',
      icon: <SmartToyIcon sx={{ fontSize: 28, color: 'secondary.main' }} />,
      skills: [
        {
          name: 'Python & FastAPI',
          level: 80,
          icon: <ApiIcon color="secondary" />,
          experience: '2+ years'
        },
        {
          name: 'Machine Learning',
          level: 70,
          icon: <SmartToyIcon color="secondary" />,
          experience: '1+ year'
        },
        {
          name: 'PostgreSQL & MongoDB',
          level: 65,
          icon: <StorageIcon color="secondary" />,
          experience: '1+ year'
        }
      ]
    },
    blockchain: {
      title: 'Blockchain & Web3',
      icon: <AccountBalanceIcon sx={{ fontSize: 28, color: 'warning.main' }} />,
      skills: [
        {
          name: 'Solidity & Smart Contracts',
          level: 70,
          icon: <SecurityIcon color="warning" />,
          experience: '1+ year'
        },
        {
          name: 'Web3.js & Ethers.js',
          level: 65,
          icon: <ApiIcon color="warning" />,
          experience: '8+ months'
        },
        {
          name: 'DeFi Protocols',
          level: 60,
          icon: <AccountBalanceIcon color="warning" />,
          experience: '6+ months'
        }
      ]
    },
    devops: {
      title: 'DevOps & Cloud',
      icon: <CloudIcon sx={{ fontSize: 28, color: 'info.main' }} />,
      skills: [
        {
          name: 'Docker & Kubernetes',
          level: 55,
          icon: <CloudIcon color="info" />,
          experience: '6+ months'
        },
        {
          name: 'AWS & Vercel',
          level: 60,
          icon: <CloudIcon color="info" />,
          experience: '8+ months'
        },
        {
          name: 'CI/CD & GitHub Actions',
          level: 50,
          icon: <ApiIcon color="info" />,
          experience: '4+ months'
        }
      ]
    }
  };

  const technologies = [
    { name: 'React', icon: <DataObjectIcon />, category: 'frontend' },
    { name: 'TypeScript', icon: <CodeIcon />, category: 'frontend' },
    { name: 'Python', icon: <SmartToyIcon />, category: 'backend' },
    { name: 'Solidity', icon: <SecurityIcon />, category: 'blockchain' },
    { name: 'AWS', icon: <CloudIcon />, category: 'devops' },
    { name: 'Docker', icon: <StorageIcon />, category: 'devops' },
    { name: 'PostgreSQL', icon: <StorageIcon />, category: 'backend' },
    { name: 'Next.js', icon: <ApiIcon />, category: 'frontend' }
  ];

  const interests = [
    'Artificial Intelligence',
    'Machine Learning',
    'Blockchain Technology',
    'DeFi Protocols',
    'Smart Contracts',
    'React Development',
    'TypeScript',
    'Full-Stack Development'
  ];

  return (
    <SectionContainer>
      <BlockchainNetwork />
      
      <ContentWrapper>
        <Box sx={{ maxWidth: '1400px', mx: 'auto', px: 4 }}>
          <Typography variant="h2" textAlign="center" gutterBottom sx={{ mb: 2 }}>
          Hakkımda & Yeteneklerim
        </Typography>
          <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 8, maxWidth: '600px', mx: 'auto' }}>
            Teknoloji tutkunu bir geliştiriciyim. Modern web teknolojileri, AI ve blockchain alanlarında uzmanlaşıyorum.
        </Typography>
        
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={8}>
          {/* About Text */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
              Merhaba! 👋
            </Typography>
              <Typography variant="body1" paragraph sx={{ mb: 3, lineHeight: 1.8 }}>
              Bilgisayar mühendisliği öğrencisi olarak AI ve Web3 teknolojilerine tutkuyla odaklanıyorum. 
              Modern web uygulamaları geliştirirken, blockchain teknolojileri ve yapay zeka alanlarında 
              kendimi sürekli geliştiriyorum.
            </Typography>
              <Typography variant="body1" paragraph sx={{ mb: 4, lineHeight: 1.8 }}>
              Özellikle React, TypeScript ve Python kullanarak projeler geliştiriyorum. 
              DeFi protokolleri, smart contract'lar ve machine learning modelleri ile çalışmayı seviyorum.
            </Typography>
            
              {/* Technology Icons */}
              <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 3 }}>
                Teknolojiler
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', 
                gap: 3, 
                mb: 4 
              }}>
                {technologies.map((tech, index) => (
                  <TechIconComponent
                    key={index}
                    icon={tech.icon}
                    name={tech.name}
                    isActive={activeTech === tech.name}
                    onClick={() => {
                      setActiveTech(activeTech === tech.name ? null : tech.name);
                      setActiveCategory(tech.category);
                    }}
                  />
                ))}
              </Box>
              
              {/* Interests */}
            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              İlgi Alanlarım
            </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1.5}>
              {interests.map((interest, index) => (
                <Chip 
                  key={index}
                  label={interest}
                  variant="outlined"
                  color="primary"
                    sx={{ 
                      borderRadius: '16px',
                      '&:hover': {
                        background: 'primary.main',
                        color: 'primary.contrastText',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                />
              ))}
            </Stack>
          </Box>
          
            {/* Modern Skills Section */}
            <Box sx={{ flex: 1 }} id="skills-section">
              {/* Category Tabs */}
              <Stack direction="row" spacing={1} sx={{ mb: 4, flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(skillCategories).map(([key, category]) => (
                  <Chip
                    key={key}
                    label={category.title}
                    icon={category.icon}
                    variant={activeCategory === key ? "filled" : "outlined"}
                    color={activeCategory === key ? "primary" : "default"}
                    onClick={() => setActiveCategory(key)}
                    sx={{
                      borderRadius: '20px',
                      padding: '8px 4px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 4
                      }
                    }}
                  />
                ))}
              </Stack>

              {/* Active Skills Category */}
              <ModernSkillCard>
                <CardContent sx={{ p: 4 }}>
                  <CategoryHeader>
                    {skillCategories[activeCategory as keyof typeof skillCategories].icon}
                    <Typography variant="h5" fontWeight={700}>
                      {skillCategories[activeCategory as keyof typeof skillCategories].title}
                    </Typography>
                  </CategoryHeader>
                  
            <Stack spacing={3}>
                    {skillCategories[activeCategory as keyof typeof skillCategories].skills.map((skill, index) => (
                      <ModernSkillItem
                        key={index}
                        name={skill.name}
                        level={skill.level}
                        icon={skill.icon}
                        experience={skill.experience}
                        isVisible={skillsVisible}
                      />
                    ))}
                  </Stack>
                </CardContent>
              </ModernSkillCard>

              {/* Skills Summary */}
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  Toplam Deneyim
                </Typography>
                <Stack direction="row" spacing={4} justifyContent="center" sx={{ mb: 4 }}>
                  <Box>
                    <Typography variant="h4" color="primary.main" fontWeight={700}>
                      2+
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Yıl Deneyim
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" color="secondary.main" fontWeight={700}>
                      15+
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Teknoloji
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" color="success.main" fontWeight={700}>
                      10+
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Proje
                    </Typography>
                  </Box>
            </Stack>
                
                {/* 3D Skills Visualization removed */}
              </Box>
          </Box>
        </Stack>
        </Box>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default AboutSection;